import React, { createContext, useState, useEffect, ReactNode } from 'react'

export interface DiaDesglose {
  fecha: string
  tarifa: number
}

export interface CartItem {
  espacio_id: string
  nombre: string
  codigo: string
  tipo_nombre: string
  fecha_entrada: string  // ISO date
  fecha_salida: string   // ISO date
  unidad_reserva: 'noche' | 'dia'
  cantidad_unidades: number
  tarifa_aplicada_por_unidad: number
  monto_total: number
  desglose?: DiaDesglose[]  // Detalles de tarifa por día
  imagenes?: string[]
  capacidad_maxima?: number
  comodidades?: string[]
}

export interface CartData {
  items: CartItem[]
  monto_total: number
  cantidad_espacios: number
  booking_token?: string
  expires_at?: string
  estado: 'carrito' | 'bloqueada' | 'pagando' | 'confirmada'
}

export interface ShoppingCartContextType {
  cart: CartData
  addItem: (item: CartItem) => void
  removeItem: (espacio_id: string) => void
  updateItem: (espacio_id: string, updates: Partial<CartItem>) => void
  clearCart: () => void
  setBloqueo: (token: string, expires_at: string) => void
  setState: (estado: CartData['estado']) => void
}

export const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(undefined)

const STORAGE_KEY = 'shopping_cart'
const INITIAL_CART: CartData = {
  items: [],
  monto_total: 0,
  cantidad_espacios: 0,
  estado: 'carrito'
}

export const ShoppingCartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartData>(INITIAL_CART)

  // Cargar carrito desde localStorage al montar
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        setCart(JSON.parse(saved))
      } catch (error) {
        console.error('Error al cargar carrito de localStorage:', error)
        setCart(INITIAL_CART)
      }
    }
  }, [])

  // Guardar carrito en localStorage cada vez que cambia
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart))
  }, [cart])

  const addItem = (item: CartItem) => {
    setCart(prev => {
      // Verificar si el espacio ya existe en el carrito (sin importar fechas)
      const existe = prev.items.find(i => i.espacio_id === item.espacio_id)

      if (existe) {
        // Ya existe en el carrito, no agregar duplicado
        // El frontend debe mostrar un toast al usuario
        return prev
      }

      // Agregar nuevo espacio
      const newItems = [...prev.items, item]
      const newTotal = newItems.reduce((sum, i) => sum + i.monto_total, 0)

      return {
        ...prev,
        items: newItems,
        monto_total: newTotal,
        cantidad_espacios: newItems.length
      }
    })
  }

  const removeItem = (espacio_id: string) => {
    setCart(prev => {
      const newItems = prev.items.filter(i => i.espacio_id !== espacio_id)
      const newTotal = newItems.reduce((sum, i) => sum + i.monto_total, 0)

      return {
        ...prev,
        items: newItems,
        monto_total: newTotal,
        cantidad_espacios: newItems.length
      }
    })
  }

  const updateItem = (espacio_id: string, updates: Partial<CartItem>) => {
    setCart(prev => {
      const newItems = prev.items.map(i =>
        i.espacio_id === espacio_id ? { ...i, ...updates } : i
      )
      const newTotal = newItems.reduce((sum, i) => sum + i.monto_total, 0)

      return {
        ...prev,
        items: newItems,
        monto_total: newTotal
      }
    })
  }

  const clearCart = () => {
    setCart(INITIAL_CART)
    localStorage.removeItem(STORAGE_KEY)
  }

  const setBloqueo = (token: string, expires_at: string) => {
    setCart(prev => ({
      ...prev,
      booking_token: token,
      expires_at: expires_at,
      estado: 'bloqueada'
    }))
  }

  const setState = (estado: CartData['estado']) => {
    setCart(prev => ({
      ...prev,
      estado
    }))
  }

  return (
    <ShoppingCartContext.Provider value={{ cart, addItem, removeItem, updateItem, clearCart, setBloqueo, setState }}>
      {children}
    </ShoppingCartContext.Provider>
  )
}

// Custom hook para usar el carrito
export const useShoppingCart = () => {
  const context = React.useContext(ShoppingCartContext)
  if (context === undefined) {
    throw new Error('useShoppingCart debe ser usado dentro de un ShoppingCartProvider')
  }
  return context
}
